import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Repeat as RepeatIcon, Layers } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { toast } from "sonner";

/**
 * CounterOfferModal — opens with an existing offer and lets the recipient
 * pick a different bundle of their items + message and submit a counter.
 */
export default function CounterOfferModal({ open, onClose, offer, onCountered }) {
  const [myListings, setMyListings] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setMessage("");
    api.get("/auth/me").then(({ data }) => {
      return api.get(`/users/${data.user_id}/listings`).then(({ data }) => {
        const active = (data.items || []).filter(l => l.status === "active");
        setMyListings(active);
      });
    }).catch(() => setMyListings([]));
  }, [open]);

  if (!open || !offer) return null;
  const originalItems = offer.offered_listings || [];
  const targetItems = originalItems;

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least one item"); return; }
    setSubmitting(true);
    try {
      await api.post(`/trade-offers/${offer.id}/counter`, {
        target_listing_ids: targetItems.map(item => item.id),
        offered_listing_ids: selected,
        message,
      });
      toast.success("Counter sent");
      onClose();
      onCountered?.();
      navigate(`/messages/${offer.conversation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send counter");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-0 md:p-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="counter-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E2E2]">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <RepeatIcon className="w-5 h-5 text-[#0654BA]" /> Counter-offer
            </h3>
            <p className="text-xs text-zinc-500">Adjust the deal and send it back.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F1F5F9]" data-testid="counter-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          {/* Original */}
          {originalItems.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Original offer was</p>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {originalItems.map(l => (
                  <div key={l.id} className="flex-shrink-0 w-28 vyrti-card !shadow-none border border-[#E2E2E2] overflow-hidden">
                    <div className="aspect-square bg-[#E2E2E2]">
                      {l.images?.[0] && <img src={imgSrc(l.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <p className="p-1.5 text-[11px] line-clamp-1">{l.title}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">You want in return</p>
            <div className="space-y-2">
              {targetItems.map(target => <div key={target.id} className="vyrti-card !shadow-none border border-[#E2E2E2] p-3 flex items-center gap-3">
                {target.images?.[0] && <img src={imgSrc(target.images[0])} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                <p className="font-medium truncate">{target.title}</p>
              </div>)}
            </div>
          </div>

          {/* New bundle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-[#0654BA]" /> Your new bundle
              </p>
              <span className="vyrti-pill !text-[11px] !py-0.5">{selected.length} selected</span>
            </div>
            {myListings.length === 0 ? (
              <div className="vyrti-card p-6 text-center !shadow-none border border-dashed border-zinc-200">
                <p className="text-sm text-zinc-600">You don&apos;t have any active listings to offer.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
                {myListings.map(l => {
                  const active = selected.includes(l.id);
                  return (
                    <button key={l.id} onClick={() => toggle(l.id)} className={`text-left rounded-2xl border-2 transition-all overflow-hidden ${active ? "border-[#0654BA] shadow-[0_8px_24px_rgba(59,130,246,0.18)]" : "border-[#E2E2E2] hover:border-zinc-300"}`} data-testid={`counter-offer-${l.id}`}>
                      <div className="aspect-[4/3] bg-[#E2E2E2]">
                        {l.images?.[0] && <img src={imgSrc(l.images[0])} alt={l.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="p-2">
                        <p className="text-sm font-medium line-clamp-1">{l.title}</p>
                        <p className="text-[11px] text-zinc-500 line-clamp-1">{l.category}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Add a note (optional)</p>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="input-text min-h-[80px] resize-y"
              placeholder="What changed in your counter?"
              data-testid="counter-message"
            />
          </div>
        </div>

        <div className="p-5 border-t border-[#E2E2E2] flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-pill btn-soft !text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || selected.length === 0}
            className="btn-pill btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="counter-submit"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending…" : "Send counter"}
          </button>
        </div>
      </div>
    </div>
  );
}
